/**
 * REV-796: the one place the UI formats dates, times, numbers, percentages,
 * relative times and lists.
 *
 * Every formatter is bound to the ACTIVE i18next language at call time, never
 * to the browser's locale. Before this, thirteen `toLocaleDateString()` calls
 * passed `undefined` (the browser's locale) or nothing at all, so a user who
 * chose German in the switcher still saw `10/2/2026`.
 *
 * Usage:
 *   // In a component (re-renders when the language changes):
 *   const { date, number } = useFormat();
 *   <span>{date(item.createdAt)}</span>
 *
 *   // Outside React, or in a module-level helper:
 *   import { formatDate } from '@utils/format';
 *   formatDate('2026-10-02T00:00:00Z');   // en: 10/2/2026   de: 2.10.2026
 *
 * `Intl` formatters are costly to construct, so they are cached per
 * locale + options. A direct `toLocale*String` / `Intl.*` call anywhere else in
 * src/ fails `format.guard.test.ts`.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const FALLBACK_LOCALE = 'en';

// tsconfig's lib predates Intl.ListFormat (ES2021); every supported browser and
// Node 22 have it, so type the little we use rather than widen the lib for the repo.
interface ListFormatOptions {
  type?: 'conjunction' | 'disjunction' | 'unit';
  style?: 'long' | 'short' | 'narrow';
}
interface ListFormatLike {
  format(items: Iterable<string>): string;
}

/**
 * The BCP 47 tag formatters should use right now. i18next may hold a tag the
 * runtime's ICU cannot canonicalise (a corrupt localStorage value is enough),
 * and `new Intl.*(badTag)` throws a RangeError, which would take a whole page
 * down over a date. Fall back to English instead.
 */
export function activeLocale(language?: string): string {
  // `language`, NOT `resolvedLanguage`: the latter is the language a translation
  // was found in, so it stays 'en' for a chosen 'de' until de resources exist.
  const candidate = language ?? i18n.language;
  if (!candidate) return FALLBACK_LOCALE;
  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? FALLBACK_LOCALE;
  } catch {
    return FALLBACK_LOCALE;
  }
}

type Ctor<T> = new (locale: string, options?: never) => T;

const cache = new Map<string, unknown>();

function cached<T>(kind: string, Ctor: Ctor<T>, locale: string, options: object | undefined): T {
  const key = `${kind}|${locale}|${options ? JSON.stringify(options) : ''}`;
  let hit = cache.get(key) as T | undefined;
  if (!hit) {
    hit = new Ctor(locale, options as never);
    cache.set(key, hit);
  }
  return hit;
}

/** Shown when a value is missing or not a valid date/number. */
const EMPTY = '—';

export type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATE_DEFAULTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};
const TIME_DEFAULTS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: 'numeric' };

interface FormatOptions {
  /** Returned when the value is missing or invalid. Defaults to an em dash. */
  fallback?: string;
}

export type DateFormatOptions = Intl.DateTimeFormatOptions & FormatOptions;

function formatWith(
  value: DateInput,
  defaults: Intl.DateTimeFormatOptions,
  options: DateFormatOptions | undefined,
  language?: string
): string {
  const { fallback = EMPTY, ...intlOptions } = options ?? {};
  const d = toDate(value);
  if (!d) return fallback;
  // Explicit options replace the defaults wholesale: merging `{month:'short'}`
  // into numeric defaults would yield a month named twice.
  const resolved = Object.keys(intlOptions).length > 0 ? intlOptions : defaults;
  return cached('dtf', Intl.DateTimeFormat, activeLocale(language), resolved).format(d);
}

/** Date only. Default is the numeric short form: en `10/2/2026`, de `2.10.2026`. */
export function formatDate(value: DateInput, options?: DateFormatOptions, language?: string) {
  return formatWith(value, DATE_DEFAULTS, options, language);
}

/** Time only: en `3:04 PM`, de `15:04`. */
export function formatTime(value: DateInput, options?: DateFormatOptions, language?: string) {
  return formatWith(value, TIME_DEFAULTS, options, language);
}

/** Date and time: en `10/2/2026, 3:04 PM`, de `2.10.2026, 15:04`. */
export function formatDateTime(value: DateInput, options?: DateFormatOptions, language?: string) {
  return formatWith(value, { ...DATE_DEFAULTS, ...TIME_DEFAULTS }, options, language);
}

export type NumberFormatOptions = Intl.NumberFormatOptions & FormatOptions;

/** Decimal number with the locale's grouping: en `1,234.5`, de `1.234,5`. */
export function formatNumber(
  value: number | null | undefined,
  options?: NumberFormatOptions,
  language?: string
): string {
  const { fallback = EMPTY, ...intlOptions } = options ?? {};
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return cached('nf', Intl.NumberFormat, activeLocale(language), intlOptions).format(value);
}

/**
 * A FRACTION as a percentage: 0.256 -> en `26%`, de `26 %` (de puts a
 * no-break space before the sign). Pass `{ maximumFractionDigits: 1 }` for more.
 */
export function formatPercent(
  fraction: number | null | undefined,
  options?: NumberFormatOptions,
  language?: string
): string {
  return formatNumber(fraction, { style: 'percent', ...options }, language);
}

/** Currency from a major-unit amount: en `$12.50`, de `12,50 $`. */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string,
  options?: NumberFormatOptions,
  language?: string
): string {
  return formatNumber(amount, { style: 'currency', currency, ...options }, language);
}

const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600],
  ['month', 30 * 24 * 3600],
  ['day', 24 * 3600],
  ['hour', 3600],
  ['minute', 60],
];

/**
 * "3 days ago" / "vor 3 Tagen". Picks the largest unit that fits. `now` is
 * injected so callers and tests are deterministic. `numeric: 'auto'` gives
 * "yesterday" / "gestern" rather than "1 day ago".
 */
export function formatRelativeTime(
  value: DateInput,
  now: Date = new Date(),
  options?: Intl.RelativeTimeFormatOptions & FormatOptions,
  language?: string
): string {
  const { fallback = EMPTY, ...intlOptions } = options ?? {};
  const d = toDate(value);
  if (!d) return fallback;
  const rtf = cached<Intl.RelativeTimeFormat>(
    'rtf',
    Intl.RelativeTimeFormat as unknown as Ctor<Intl.RelativeTimeFormat>,
    activeLocale(language),
    { numeric: 'auto', ...intlOptions }
  );
  const seconds = Math.round((d.getTime() - now.getTime()) / 1000);
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.trunc(seconds / size), unit);
  }
  return rtf.format(seconds, 'second');
}

/** "a, b, and c" / "a, b und c". */
export function formatList(
  items: readonly string[],
  options?: ListFormatOptions,
  language?: string
): string {
  return cached<ListFormatLike>(
    'lf',
    (Intl as unknown as { ListFormat: Ctor<ListFormatLike> }).ListFormat,
    activeLocale(language),
    { type: 'conjunction', style: 'long', ...options }
  ).format(items);
}

/**
 * The same formatters bound to the language the component is rendering in.
 * `useTranslation` subscribes to `languageChanged`, so a component using this
 * re-renders when the user switches language.
 */
export function useFormat() {
  const { i18n: instance } = useTranslation();
  const language = instance.language;
  return useMemo(
    () => ({
      locale: activeLocale(language),
      date: (v: DateInput, o?: DateFormatOptions) => formatDate(v, o, language),
      time: (v: DateInput, o?: DateFormatOptions) => formatTime(v, o, language),
      dateTime: (v: DateInput, o?: DateFormatOptions) => formatDateTime(v, o, language),
      number: (v: number | null | undefined, o?: NumberFormatOptions) =>
        formatNumber(v, o, language),
      percent: (v: number | null | undefined, o?: NumberFormatOptions) =>
        formatPercent(v, o, language),
      currency: (v: number | null | undefined, c: string, o?: NumberFormatOptions) =>
        formatCurrency(v, c, o, language),
      relativeTime: (
        v: DateInput,
        now?: Date,
        o?: Intl.RelativeTimeFormatOptions & FormatOptions
      ) => formatRelativeTime(v, now, o, language),
      list: (items: readonly string[], o?: ListFormatOptions) => formatList(items, o, language),
    }),
    [language]
  );
}

/**
 * REV-684: the browser's own IANA timezone, for the profile default set at
 * registration. Not a formatter, but it reads Intl.DateTimeFormat, which only
 * this file may (format.guard.test.ts).
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
